import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Checkbox } from '_layout/form-elements'
import Falafel from '_images/menu-dots.svg'
import handleClickOutside from '_utils/handleClickOutside'
import handleKeyPress from '_root/utils/handleKeyPress'
import { getHidingParent } from '_services/util'

//#region Styles
const Content = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
  box-shadow: 0px 2px 0px var(--color-line);
  width: ${props => props.$width ? props.$width : 'min(300px, 100%)'};

  input[type=text] {
    width: 100%;
  }

  &:hover {
    box-shadow: 0px 2px 0px var(--color-line-dark);
  }
`
const Dropdown = styled.div`
    display: flex;
    flex-flow: column nowrap;
    position: absolute;
    left: 0;
    min-width: 225px;
    width: 100%;
    background: var(--color-bg-white);
    border: 1px solid var(--color-line-dark); 
    box-shadow: 2px 2px 10px var(--color-line-dark);
    z-index: 10000;

    &:focus {
        outline: none;
    }
`
const ObjectList = styled.ul`
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style-type: none;
`
const ListItem = styled.li`
    color: var(--color-text-dark);
    list-style-type: none;

    > label {
        cursor: pointer;
        position: relative;
        display: block;
        padding: 10px 10px 10px 36px;

        > label {
            position: absolute;
            top: 50%;
            left: 10px;
            transform: translateY(-50%);
        }
    }

    &.no-objects {
      padding: 10px;
    }

    &:nth-child(even) {
      background: var(--color-bg-lightest);
    }

    &:hover {
      color: var(--color-text-white);
      background: var(--color-nav-1);
    }
`
const Title = styled.h4`
    flex: none;
    margin: 0;
    padding: 5px 10px;
    font-weight: 400;
    color: var(--color-text-white);
    background: var(--color-nav-2);
`
const Input = styled.input`
    cursor: pointer;
    width: 100%;
    background: url(${Falafel}) scroll 100% 50% no-repeat var(--color-bg-white);
    background-size: 24px 16px;

    &::placeholder {
      color: var(--color-text-dark);
    }

    &[type=text] {
        padding-right: 10%;
    }

    &:focus {
      cursor: text;
      }
      
      &[disabled] {
        background: var(--color-bg-lightest);
        }
        `
        //#endregion
    
const MultiObjectPicker = ({ selectedText = 'objekt vald(a)', noObjects = 'Inga valbara objekt', noSelected = 'Inga objekt valda', objects = [], selected = [], placeholder = 'Välj objekt...', width, count, selectedTitle = 'Valda objekt', onSelected }) => {

  const [showList, setShowList] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [filteredObjects, setFilteredObjects] = useState(objects)
  const [selectedObjects, setSelectedObjects] = useState(selected)
  const [dropStyle, setDropdownStyle] = useState({})
  const [sublistStyle, setSublistStyle] = useState({})
  
  const textInputRef = useRef(null)
  const dropdownRef = useRef(null)
  
  const selectedObjectsText = selectedObjects.map((obj) => obj.name).join(', ')
  
  const selectedObjectsCount = selectedObjects.length > 0
    ? count
      ? `${selectedObjects.length} ${selectedText}`
      : selectedObjectsText
    : placeholder
      
  // Closes dropdown on Escape, Enter or Tab
  useEffect(() => {
    if (!showList) return

    const cleanup = handleKeyPress(['Escape', 'Enter', 'Tab'], closeDropDown)
    return cleanup
  }, [showList])

  // Closes dropdown when clicking outside
  useEffect(() => {
    if (!showList) return

    const cleanup = handleClickOutside([dropdownRef, textInputRef], closeDropDown)
    return cleanup
  }, [showList])

  // Filters objects based on search text and excludes alredy selected objects
  useEffect(() => {
    const lowerCaseFilterText = filterText.toLowerCase()

    setFilteredObjects(
      objects.filter(
        (obj) =>
          obj.name.toLowerCase().includes(lowerCaseFilterText) &&
          !selectedObjects.some((sel) => sel.id === obj.id)
      )
    )
  }, [filterText, objects, selectedObjects])
      
  const inputFocus = () => {
    if (textInputRef.current) {
      const hidingParent = getHidingParent(textInputRef.current)
      const docHeight = hidingParent ? hidingParent.offsetHeight : document.body.offsetHeight
      const inputRect = textInputRef.current.getBoundingClientRect()
      const topOffset = inputRect.top - (hidingParent ? hidingParent.getBoundingClientRect().top : 0)
      const showListAbove = topOffset > docHeight / 2
      const listMaxHeight = (showListAbove ? topOffset : docHeight - topOffset - inputRect.height) - 10
      const calculatedDropStyle = showListAbove
        ? { maxHeight: listMaxHeight, bottom: '100%' }
        : { maxHeight: listMaxHeight, top: '100%' }
      const calculatedSublistStyle = { maxHeight: (listMaxHeight / 2) - 28 }

      setDropdownStyle(calculatedDropStyle)
      setSublistStyle(calculatedSublistStyle)
      setShowList(true)
    }
  }

  const closeDropDown = () => {
    setShowList(false)
    setFilterText('')
    textInputRef.current.blur()
  }

  const selectObject = (objectId) => {
    const object = objects.find((obj) => obj.id === objectId)
    if (!object) return

    const updatedSelected = [...selectedObjects, object].sort((a, b) =>
      a.name.localeCompare(b.name))

    setSelectedObjects(updatedSelected)

    setFilteredObjects((prevFiltered) =>
      prevFiltered.filter((obj) => obj.id !== objectId))

    setFilterText('')

    if (onSelected) onSelected(updatedSelected)
  }

  const removeObject = (objectId) => {
    const updatedSelected = selectedObjects.filter((obj) => obj.id !== objectId)
    setSelectedObjects(updatedSelected)

    const removedObject = objects.find((obj) => obj.id === objectId)
    if (removedObject) {
      setFilteredObjects((prevFiltered) => [...prevFiltered, removedObject])
    }

    if (onSelected) onSelected(updatedSelected)
  }

  return (
    <Content $width={width}>
      <Input
        type="text"
        placeholder={selectedObjectsCount || selectedObjectsText || placeholder}
        ref={textInputRef}
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        onFocus={inputFocus}
      />
      {showList && (
        <Dropdown
          tabIndex="1"
          className="object-picker-dropdown"
          ref={dropdownRef}
          style={dropStyle}
        >
          {selectedObjects.length > 0 && (
            <>
              <Title>{selectedTitle}</Title>
              <ObjectList style={sublistStyle}>
                {selectedObjects.map((object) => (
                  <ListItem
                    key={object.id}
                    title="Klicka för att återställa valt objekt"
                  >
                    <label>
                      <Checkbox
                        tabIndex="0"
                        className="object-picker-dropdown-input"
                        value={object.id}
                        onChange={() => removeObject(object.id)}
                        checked={true}
                      />
                      <span>{object.name}</span>
                    </label>
                  </ListItem>
                ))}
              </ObjectList>
            </>
          )}
          {selectedObjects.length === 0 && (
            <>
              <Title>{selectedTitle}</Title>
              <ListItem key="0" className="no-objects">
                {noSelected}
              </ListItem>
            </>
          )}
          <Title>{placeholder}</Title>
          <ObjectList style={sublistStyle}>
            {filteredObjects.length > 0 ? (
              filteredObjects.map((object) => (
                <ListItem
                  key={object.id}
                  title="Klicka för att välja objekt"
                >
                  <label>
                    <Checkbox
                      tabIndex="0"
                      className="object-picker-dropdown-input"
                      value={object.id}
                      onChange={() => selectObject(object.id)}
                    />
                    <span>{object.name}</span>
                  </label>
                </ListItem>
              ))
            ) : (
              <ListItem key="0" className="no-objects">
                {noObjects}
              </ListItem>
            )}
          </ObjectList>
        </Dropdown>
      )}
    </Content>
  )
}

export default MultiObjectPicker